const INITIAL_VISIBLE_CARDS = 3;
const EXPAND_COLLAPSE_DURATION_MS = 360;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function getPeekHeight(root: HTMLElement): number {
  const value = getComputedStyle(root).getPropertyValue('--solution-area-products-grid-peek');
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 72;
}

function getCollapsedHeight(viewport: HTMLElement, cards: HTMLElement[]): number | null {
  const lastVisibleCard = cards[INITIAL_VISIBLE_CARDS - 1];
  if (!lastVisibleCard) return null;
  const viewportRect = viewport.getBoundingClientRect();
  const firstRowBottom = lastVisibleCard.getBoundingClientRect().bottom;
  const peekHeight = getPeekHeight(viewport);
  const { paddingBlockEnd } = getComputedStyle(viewport);
  const paddingBottom = Number.parseFloat(paddingBlockEnd) || 0;
  return firstRowBottom - viewportRect.top + peekHeight + paddingBottom;
}

function setCollapsedHeight(viewport: HTMLElement, cards: HTMLElement[]): boolean {
  const collapsedHeight = getCollapsedHeight(viewport, cards);
  if (collapsedHeight === null) return false;
  viewport.style.setProperty('--grid-collapsed-height', `${collapsedHeight}px`);
  viewport.style.maxBlockSize = `${collapsedHeight}px`;
  return true;
}

function setHiddenCardsInteractiveState(cards: HTMLElement[], expanded: boolean): void {
  cards.forEach((card, index) => {
    if (index < INITIAL_VISIBLE_CARDS) {
      card.removeAttribute('inert');
      card.removeAttribute('aria-hidden');
      return;
    }

    if (expanded) {
      card.removeAttribute('inert');
      card.removeAttribute('aria-hidden');
      return;
    }

    card.setAttribute('inert', '');
    card.setAttribute('aria-hidden', 'true');
  });
}

function applyCollapsed(viewport: HTMLElement, cards: HTMLElement[]): void {
  viewport.setAttribute('data-collapsed', '');
  setCollapsedHeight(viewport, cards);
}

function mountArea(area: HTMLElement): void {
  const viewport = area.querySelector<HTMLElement>('[data-solution-area-products-grid-viewport]');
  const grid = area.querySelector<HTMLElement>('[data-solution-area-products-grid]');
  const cards = Array.from(
    area.querySelectorAll<HTMLElement>('[data-solution-area-products-card]')
  );
  const toggleWrap = area.querySelector<HTMLElement>('[data-solution-area-products-toggle-wrap]');
  const toggleButton = area.querySelector<HTMLButtonElement>(
    '[data-solution-area-products-toggle]'
  );
  const toggleLabel = area.querySelector<HTMLElement>('[data-solution-area-products-toggle-label]');

  area.addEventListener('click', event => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('a, button, input, select, textarea, [role="button"], [role="link"]')) {
      return;
    }

    const card = target.closest<HTMLElement>('[data-product-card-link]');
    const url = card?.dataset.productCardLink;
    if (!url) return;

    window.location.href = url;
  });

  if (cards.length === 0) {
    area.hidden = true;
    return;
  }

  if (
    !viewport ||
    !grid ||
    !toggleWrap ||
    !toggleButton ||
    !toggleLabel ||
    cards.length <= INITIAL_VISIBLE_CARDS
  ) {
    toggleWrap?.setAttribute('hidden', '');
    return;
  }

  const moreLabel =
    toggleButton.dataset.moreLabel ?? toggleLabel.textContent?.trim() ?? 'View more products';
  const lessLabel = toggleButton.dataset.lessLabel ?? 'View less products';

  let expanded = false;
  let transitionTimeout: number | null = null;

  const clearTransitionState = (): void => {
    if (transitionTimeout !== null) {
      window.clearTimeout(transitionTimeout);
      transitionTimeout = null;
    }

    viewport.removeAttribute('data-expanding');
    viewport.removeAttribute('data-collapsing');
  };

  const finishExpanded = (): void => {
    clearTransitionState();
    viewport.removeAttribute('data-collapsed');
    viewport.style.removeProperty('--grid-collapsed-height');
    viewport.style.maxBlockSize = 'none';
  };

  const finishCollapsed = (): void => {
    clearTransitionState();
    viewport.setAttribute('data-collapsed', '');
    setCollapsedHeight(viewport, cards);
  };

  const animateExpanded = (): void => {
    const collapsedHeight = getCollapsedHeight(viewport, cards);
    if (collapsedHeight === null) return;

    clearTransitionState();
    viewport.style.setProperty('--grid-collapsed-height', `${collapsedHeight}px`);
    viewport.style.maxBlockSize = `${collapsedHeight}px`;
    viewport.setAttribute('data-collapsed', '');
    viewport.setAttribute('data-expanding', '');

    requestAnimationFrame(() => {
      viewport.style.maxBlockSize = `${viewport.scrollHeight}px`;
    });

    transitionTimeout = window.setTimeout(finishExpanded, EXPAND_COLLAPSE_DURATION_MS);
  };

  const animateCollapsed = (): void => {
    clearTransitionState();
    viewport.style.maxBlockSize = `${viewport.scrollHeight}px`;
    viewport.setAttribute('data-collapsed', '');
    viewport.setAttribute('data-collapsing', '');

    requestAnimationFrame(() => {
      setCollapsedHeight(viewport, cards);
    });

    transitionTimeout = window.setTimeout(finishCollapsed, EXPAND_COLLAPSE_DURATION_MS);
  };

  const update = (): void => {
    setHiddenCardsInteractiveState(cards, expanded);

    if (prefersReducedMotion()) {
      clearTransitionState();

      if (!expanded) {
        applyCollapsed(viewport, cards);
      } else {
        viewport.removeAttribute('data-collapsed');
        viewport.style.removeProperty('--grid-collapsed-height');
        viewport.style.maxBlockSize = 'none';
      }
    } else if (!expanded) {
      if (viewport.hasAttribute('data-collapsed')) {
        animateCollapsed();
      } else {
        applyCollapsed(viewport, cards);
      }
    } else {
      animateExpanded();
    }

    toggleButton.setAttribute('aria-expanded', String(expanded));
    toggleLabel.textContent = expanded ? lessLabel : moreLabel;
  };

  toggleButton.addEventListener('click', () => {
    expanded = !expanded;
    update();
  });

  const ro = new ResizeObserver(() => {
    if (!expanded) {
      applyCollapsed(viewport, cards);
      return;
    }

    if (!prefersReducedMotion()) {
      viewport.style.maxBlockSize = `${viewport.scrollHeight}px`;
    }
  });
  ro.observe(grid);

  update();
}

export default function solutionAreaProducts(root: HTMLElement): void {
  const areas = Array.from(
    root.querySelectorAll<HTMLElement>('[data-solution-area-products-area]')
  );

  areas.forEach(area => mountArea(area));

  const visibleAreas = areas.filter(area => !area.hidden);

  if (visibleAreas.length === 0) {
    root.hidden = true;
  }
}
